import { assetsApi } from '@/api/assets'
import { listAssets, registerProductionAsset, type ProductionAsset } from '@/api/studio-production'
import type { SilkAsset } from '@/api/types'
import { silkKindFromMime } from '@/features/studio/lib/canvas-media-persist'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import type { HandleDataType, StudioFlowNode } from '@/features/studio/canvas/types'

export type DockLibraryAsset = {
  id: string
  publicId: string
  name: string
  url: string
  mime?: string
  kind: string
  createdAt?: string
}

function silkToDock(asset: SilkAsset): DockLibraryAsset {
  return {
    id: String(asset.id),
    publicId: asset.public_id,
    name: asset.filename || asset.public_id,
    url: asset.url,
    mime: asset.mime_type,
    kind: asset.kind,
    createdAt: asset.created_at,
  }
}

function productionToDock(asset: ProductionAsset): DockLibraryAsset | null {
  if (asset.status === 'archived') return null
  if (asset.registered === false) return null
  const url = asset.media_url || asset.url
  if (!url) return null
  return {
    id: asset.id,
    publicId: asset.public_id || asset.id,
    name: asset.name,
    url,
    mime: asset.mime_type,
    kind: asset.kind,
    createdAt: asset.created_at,
  }
}

export async function loadDockLibraryAssets(opts: {
  isCreative: boolean
  productionId: string
}): Promise<DockLibraryAsset[]> {
  if (opts.isCreative) {
    const res = await assetsApi.list({ limit: 200, offset: 0 })
    return sortDockLibrary((res.items ?? []).map(silkToDock))
  }
  const res = await listAssets(opts.productionId)
  return sortDockLibrary(
    (res.items ?? []).map(productionToDock).filter((row): row is DockLibraryAsset => row != null),
  )
}

function sortDockLibrary(items: DockLibraryAsset[]): DockLibraryAsset[] {
  return [...items].sort((a, b) => {
    const ta = Date.parse(a.createdAt ?? '') || 0
    const tb = Date.parse(b.createdAt ?? '') || 0
    return tb - ta
  })
}

export function uploadNodeUsesAsset(node: StudioFlowNode, asset: DockLibraryAsset): boolean {
  if (node.data.nodeType !== 'upload') return false
  const pid = node.data.upload?.assetPublicId
  if (pid && (pid === asset.publicId || pid === asset.id)) return true
  const url = node.data.upload?.url || node.data.output?.previewUrl
  return Boolean(url && asset.url && url === asset.url)
}

export function libraryAssetDataType(asset: DockLibraryAsset): HandleDataType {
  const mime = (asset.mime ?? '').toLowerCase()
  if (asset.kind === 'video' || asset.kind === 'take_video' || mime.startsWith('video/')) return 'video'
  if (asset.kind === 'audio' || mime.startsWith('audio/')) return 'audio'
  return 'image'
}

/** 生成结果写入持久资产库（失败不阻断画布） */
export async function persistGeneratedPreviewToLibrary(opts: {
  isCreative: boolean
  productionId: string
  url: string
  filename: string
  productionKind: string
}): Promise<void> {
  if (opts.isCreative) {
    const display = resolveMediaDisplayUrl(opts.url)
    const res = await fetch(display)
    if (!res.ok) throw new Error(`fetch media failed: ${res.status}`)
    const blob = await res.blob()
    const file = new File([blob], opts.filename, { type: blob.type || 'application/octet-stream' })
    await assetsApi.upload(file, silkKindFromMime(file.type))
    return
  }
  await registerProductionAsset(opts.productionId, {
    url: opts.url,
    name: opts.filename,
    kind: opts.productionKind,
    source: 'canvas',
  })
}
